import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import { formatSVG } from '@aytm/app/stats/lib/render/svg-export'
import { SETTINGS } from '@aytm/app/stats/page/question-views/psc/lib/constants'
import {
  useExportableChart,
  svgToPngDataUrl
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'
import { linkRowFromShape } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/pointHalo'

// components
import ChartRow from '@aytm/app/stats/page/question-views/psc/components/ChartRow'

import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'

// styles
import style from '@aytm/app/stats/page/question-views/psc/components/styles/Chart.module.scss'
import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'
import { useRenderGate } from '@aytm/app/stats/page/question-views/components/charts/placeholders/lib/render-gate'

const DEFAULT_WIDTH = 750

const exportOptions = [
  {
    text: `Download PNG image`,
    key: 'png'
  },
  {
    text: 'Download JPEG image',
    key: 'jpeg'
  },
  {
    text: 'Download PDF document',
    key: 'pdf'
  },
  {
    text: 'Download EPS file',
    key: 'eps'
  }
]

export default function PolarityScaleChart({
  id,
  mode,
  batches,
  palette,
  chartData,
  percentDecimals,
  onExport,
  apiRef
}) {
  const { componentRef, width } = useComponentWidth({ defaultWidth: DEFAULT_WIDTH })

  const svgRef = useRef(null)

  const [tooltip, setTooltip] = useState(null)

  // the rows are plain JSX, so the svg is final as soon as it is mounted
  const chartRendered = useRenderGate()
  useEffect(() => {
    chartRendered()
  }, [chartRendered, batches, chartData, width])

  const height = useMemo(
    () => (SETTINGS.barHeight + 2 * SETTINGS.barMargin) * batches.length + 2 * SETTINGS.chartMargin,
    [batches]
  )

  // Headless export: single-output. The whole chart is one synchronously-rendered
  // <svg> (rows are plain JSX, no D3/animation), so serializing the ref after the
  // hook's rAF is already at final positions.
  useExportableChart({
    deps: [batches?.length, chartData?.length],
    prepareForExport: async () => {
      const svgEl = svgRef.current
      if (!svgEl) throw new Error('PSC: SVG ref not attached')
      const w = svgEl.width.baseVal.value || DEFAULT_WIDTH
      const h = svgEl.height.baseVal.value || height
      const svgString =
        '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' + new XMLSerializer().serializeToString(svgEl)
      return [await svgToPngDataUrl(svgString, w, h)]
    }
  })

  const onEnter = (node, title, value, pointId) => {
    // the other half of the echo: the segment under the pointer tints the row that names it
    linkRowFromShape(node, pointId)

    const host = componentRef.current?.getBoundingClientRect()
    const r = node.getBoundingClientRect()
    if (!host) return

    setTooltip({
      x: r.left - host.left + r.width / 2,
      y: r.top - host.top - 10,
      title,
      value
    })
  }

  const onLeave = () => {
    linkRowFromShape(componentRef.current, null)
    setTooltip(null)
  }

  const onExportClick = useCallback(
    (fileType, callback) => {
      const data = (null !== svgRef.current ? svgRef.current.cloneNode(true) : void 0) || null

      onExport(
        {
          fileType
        },
        data,
        callback
      )
    },
    [onExport]
  )

  // PPTX bridge (pptx.coffee -> block.formatSVG). Serializes the same clone the Download button
  // produces (lib/svg-export formatSVG); coffee just forwards the string.
  if (apiRef) {
    apiRef.current.formatSVG = () => formatSVG(svgRef.current?.cloneNode(true) || null)
    // the on-screen size the serialized svg was measured at, so the exporter rasterizes 1:1
    apiRef.current.getSize = () => ({ width, height })
  }

  return (
    <div ref={componentRef} className={style.component}>
      <div className={style.export}>
        <ExportDropdown options={exportOptions} onExport={onExportClick} />
      </div>

      <div id={`chart_question_${id}`}>
        <svg
          ref={svgRef}
          width={width}
          height={height}
          version="1.1"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          xmlns="http://www.w3.org/2000/svg"
          viewBox={`0 0 ${width} ${height}`}
        >
          <defs>
            {/* Segments square off where they meet a neighbour, so a segment landing on the
                track's rounded end would overflow it -- clip every row to the track shape. */}
            <clipPath id={`group-${id}-track-clip`}>
              <rect
                x="0"
                y="0"
                width={Math.max(0, width - 2 * (SETTINGS.poleLabelWidth + SETTINGS.poleLabelGap))}
                height={SETTINGS.barHeight}
                rx={SETTINGS.barRadius}
              />
            </clipPath>
          </defs>
          <rect x="0" y="0" width={width} height={height} fill="#fff" />
          {batches.map(batch => (
            <ChartRow
              key={`b-${batch.position}`}
              id={id}
              width={width}
              mode={mode}
              batch={batch}
              data={chartData[batch.position - 1]}
              palette={palette}
              percentDecimals={percentDecimals}
              onEnter={onEnter}
              onLeave={onLeave}
            />
          ))}
        </svg>
      </div>

      {tooltip && (
        <div className={style.tooltip} style={{ left: tooltip.x, top: tooltip.y }}>
          <span className={style['tooltip-title']} dangerouslySetInnerHTML={{ __html: tooltip.title }} />
          <br />
          <span className={style['tooltip-value']} dangerouslySetInnerHTML={{ __html: tooltip.value }} />
        </div>
      )}
    </div>
  )
}

PolarityScaleChart.propTypes = {
  id: PropTypes.string,
  mode: PropTypes.string,
  batches: PropTypes.array,
  palette: PropTypes.array,
  chartData: PropTypes.array,
  percentDecimals: PropTypes.number,
  onModelChange: PropTypes.func,
  onExport: PropTypes.func
}
